import type { TradeIn, VehicleOption } from './types';

/**
 * Trade-in validation and utility functions for DealXG (Deal Exchange) integration.
 * These utilities handle validation and extraction of trade-in data from DealXG records.
 *
 * TEMPORARY FIX: This client-side trade-in validation and utility logic is a temporary solution.
 * In follow-up PRs, this logic will be moved to the server side for better
 * consistency and maintainability.
 */

export const isValidTradeIn = (dealRecord: any): { valid: boolean; errors: string[] } => {
    const result: { valid: boolean; errors: string[] } = { valid: true, errors: [] };
    const hasValidTradeSummary = isValidTradeInSummary(dealRecord);

    if (hasTradeIn(dealRecord)) {
        result.valid = true;

        if (hasMultipleTradeIns(dealRecord)) {
            result.valid = false;
            result.errors.push('Acceleron does not support multiple trade-ins');
        }

        if (!hasValidTradeSummary) {
            result.valid = false;
            result.errors.push('Properties totalPayoff and totalAllowance are required in tradeInSummary');
        }
    } else if (hasValidTradeSummary) {
        // TradeSummary is the only requirement for HMA Trade
        result.valid = true;
    } else {
        result.valid = false;
    }

    return result;
};

export const hasTradeIn = (dealRecord: any): boolean => {
    return Object.keys(dealRecord?.dealStructure?.vehicles?.tradeInVehicles?.tradeIns?.[0] || {}).length > 0;
};

export const hasMultipleTradeIns = (dealRecord: any): boolean => {
    return Object.keys(dealRecord?.dealStructure?.vehicles?.tradeInVehicles?.tradeIns || []).length > 1;
};

export const isValidTradeInSummary = (dealRecord: any): boolean => {
    const tradeInSummary = getTradeInSummary(dealRecord);
    const { totalPayoff, totalAllowance } = tradeInSummary;

    return totalPayoff !== null && totalPayoff !== undefined && totalAllowance !== null && totalAllowance !== undefined;
};

export const getTradeInSummary = (dealRecord: any): any => {
    if (hasTradeIn(dealRecord) && !dealRecord.dealStructure.vehicles.tradeInVehicles.tradeInSummary) {
        // build summary from provided tradeIns: deal exchange doesn't require tradeInSummary and isn't performing summation yet
        // TODO: consider removal upon completion of E303827: [DealXG] Auto populate the DealXG Summary sections
        const tradeIns: TradeIn[] = dealRecord.dealStructure.vehicles.tradeInVehicles.tradeIns;
        const totalAllowance = tradeIns.reduce(
            (sum: number, tradeIn: TradeIn) => sum + (tradeIn.appliedTradeAllowance || tradeIn.tradeAllowance || 0),
            0
        );
        const totalPayoff = tradeIns.reduce(
            (sum: number, tradeIn: TradeIn) => sum + (tradeIn.tradePayoffDetails?.tradePayoffAmount || 0),
            0
        );
        const totalAcv = tradeIns.reduce(
            (sum: number, tradeIn: TradeIn) => sum + (tradeIn.tradeValuation?.actualCashValue || 0),
            0
        );
        return {
            totalPayoff,
            totalAllowance,
            totalAcv: totalAcv || undefined
        };
    }
    return dealRecord?.dealStructure?.vehicles?.tradeInVehicles?.tradeInSummary || {};
};

/**
 * Following Method is copied from Shopper Platform. We need to move this to a shared
 * location so that both SCI and Shopper Platform can use the same utility method.
 * NOTE: THIS IS A TEMPORARY COPY. ONCE WE HAVE A SHARED LOCATION, THIS COPY SHOULD BE REMOVED.
 * TODO: We need to connect with kevin and get his opinion on where this method should live.
 */
export const transformTradeInVehicleOptions = (vehicleOptions: Record<string, VehicleOption>) => {
    const groupedOptions: Record<string, any> = {};

    Object.values(vehicleOptions).forEach((value) => {
        const optionName = value.details.name;
        const optionData = {
            categoryName: value.details.name,
            displayName: value.details.description,
            isSelected: true,
            id: parseInt(value.details.oemOptionCode, 10)
        };

        if (groupedOptions[optionName]) {
            // If the option name already exists, add to the options array
            groupedOptions[optionName].options.push(optionData);
        } else {
            // Create new entry with the option
            groupedOptions[optionName] = {
                name: optionName,
                options: [optionData]
            };
        }
    });

    return groupedOptions;
};
